import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, AlertCircle, UserPlus } from "lucide-react";
import Layout from "../components/Layout";
import StepIndicator from "../components/StepIndicator";
import BackButton from "../components/BackButton";
import PrimaryButton from "../components/PrimaryButton";
import { formatCPF, onlyDigits } from "../lib/format";
import { getBooking, setBooking } from "../lib/bookingState";
import { listarConvenios, criarPreCadastro } from "../lib/api";

const STEPS = ["CPF", "Cadastro", "Tipo", "Especialidade", "Profissional", "Horario", "Confirmar"];

export default function PreCadastro() {
  const navigate = useNavigate();
  const booking = getBooking();

  const [nome, setNome] = useState("");
  const [telefone, setTelefone] = useState("");
  const [nascimento, setNascimento] = useState("");
  const [genero, setGenero] = useState("");
  const [convenioId, setConvenioId] = useState("");
  const [convenios, setConvenios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingConvenios, setLoadingConvenios] = useState(true);
  const [error, setError] = useState("");

  const cpf = booking.cpf || "";
  const nascimentoISO = toISODate(nascimento);
  const valid =
    nome.trim().length >= 3 &&
    onlyDigits(cpf).length === 11 &&
    onlyDigits(telefone).length >= 10 &&
    Boolean(nascimentoISO) &&
    (genero === "M" || genero === "F");

  useEffect(() => {
    if (!cpf) {
      navigate("/agendar", { replace: true });
      return;
    }

    async function load() {
      try {
        const data = await listarConvenios();
        setConvenios(Array.isArray(data) ? data : []);
      } catch {
        setConvenios([{ id: "", nome: "Particular / Sem convênio" }]);
      } finally {
        setLoadingConvenios(false);
      }
    }

    load();
  }, [cpf, navigate]);

  function formatNascimento(value) {
    const d = onlyDigits(value).slice(0, 8);

    return d
      .replace(/(\d{2})(\d)/, "$1/$2")
      .replace(/(\d{2})(\d)/, "$1/$2");
  }

  function toISODate(value) {
    const d = onlyDigits(value);
    if (d.length !== 8) return "";

    const dia = d.slice(0, 2);
    const mes = d.slice(2, 4);
    const ano = d.slice(4, 8);

    const data = new Date(`${ano}-${mes}-${dia}T00:00:00`);
    if (Number.isNaN(data.getTime())) return "";
    if (String(data.getDate()).padStart(2, "0") !== dia) return "";
    if (data > new Date()) return "";

    return `${ano}-${mes}-${dia}`;
  }

  function formatTelefone(value) {
    const d = onlyDigits(value).slice(0, 11);

    if (d.length <= 10) {
      return d
        .replace(/(\d{2})(\d)/, "($1) $2")
        .replace(/(\d{4})(\d)/, "$1-$2");
    }

    return d
      .replace(/(\d{2})(\d)/, "($1) $2")
      .replace(/(\d{5})(\d)/, "$1-$2");
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (!valid || loading) return;

    setLoading(true);
    setError("");

    try {
      const paciente = await criarPreCadastro({
        nome: nome.trim().toUpperCase(),
        cpf: onlyDigits(cpf),
        telefone: onlyDigits(telefone),
        nascimento: nascimentoISO,
        genero,
        convenio_id: convenioId || "",
      });

      setBooking({
        cpf: onlyDigits(cpf),
        patient_id: paciente.patient_id,
        paciente_nome: paciente.nome || nome.trim().toUpperCase(),
        telefone: paciente.telefone || onlyDigits(telefone),
        email: paciente.email || "",
        convenio_id: paciente.convenio_id || convenioId || null,
        convenio_plano_id: paciente.convenio_plano_id || null,
        convenio_nome: paciente.convenio_nome || "",
        matricula: paciente.matricula || "",
        titular: paciente.titular || "",
        tem_convenio: Boolean(paciente.convenio_id || convenioId),
        exige_guia: Boolean(paciente.exige_guia),
        mensagem_guia: paciente.mensagem_guia || "",
        novo_cadastro: true,
      });

      toast.success("Pré-cadastro realizado com sucesso!");
      navigate("/agendar/tipo");
    } catch (err) {
      const detail = err?.response?.data?.detail;

      if (detail === "cpf_invalido") {
        setError("Por favor, verifique o CPF informado.");
      } else if (detail === "nascimento_invalido") {
        setError("Por favor, verifique a data de nascimento informada.");
      } else if (detail === "genero_invalido") {
        setError("Por favor, selecione o sexo.");
      } else {
        setError("Não foi possível criar o pré-cadastro agora. Procure a recepção ou tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <Layout>
      <BackButton to="/agendar" />
      <StepIndicator steps={STEPS} current={2} />

      <div className="fade-up">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#EAF5F6] text-[#3D8E92] text-xs font-semibold">
          <UserPlus size={13} /> Novo cadastro
        </div>

        <h1
          className="text-2xl sm:text-3xl tracking-tight font-bold text-slate-900 mt-3"
          style={{ fontFamily: "Manrope" }}
        >
          Faça seu pré-cadastro
        </h1>

        <p className="text-slate-600 mt-2">
          Não encontramos seu CPF. Preencha os dados abaixo para continuar o agendamento.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="mt-8 space-y-4">
        <label className="block">
          <span className="text-sm font-medium text-slate-700">CPF</span>
          <input
            type="text"
            value={formatCPF(cpf)}
            disabled
            className="mt-2 w-full h-14 px-4 rounded-xl border border-slate-200 bg-slate-50 text-slate-500 text-lg outline-none"
          />
        </label>

        <label className="block">
          <span className="text-sm font-medium text-slate-700">Nome completo</span>
          <input
            type="text"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder="Digite seu nome completo"
            className="mt-2 w-full h-14 px-4 rounded-xl border border-slate-200 bg-white text-slate-900 outline-none focus:ring-2 focus:ring-[#00357C]/30 focus:border-[#00357C] transition"
          />
        </label>

        <label className="block">
          <span className="text-sm font-medium text-slate-700">WhatsApp / Celular</span>
          <input
            type="tel"
            inputMode="numeric"
            value={telefone}
            onChange={(e) => setTelefone(formatTelefone(e.target.value))}
            placeholder="(43) 99999-9999"
            className="mt-2 w-full h-14 px-4 rounded-xl border border-slate-200 bg-white text-slate-900 outline-none focus:ring-2 focus:ring-[#00357C]/30 focus:border-[#00357C] transition"
          />
        </label>

        <label className="block">
          <span className="text-sm font-medium text-slate-700">Data de nascimento</span>
          <input
            type="text"
            inputMode="numeric"
            value={nascimento}
            onChange={(e) => setNascimento(formatNascimento(e.target.value))}
            placeholder="DD/MM/AAAA"
            className="mt-2 w-full h-14 px-4 rounded-xl border border-slate-200 bg-white text-slate-900 outline-none focus:ring-2 focus:ring-[#00357C]/30 focus:border-[#00357C] transition"
          />
        </label>

        <label className="block">
          <span className="text-sm font-medium text-slate-700">Sexo</span>
          <select
            value={genero}
            onChange={(e) => setGenero(e.target.value)}
            className="mt-2 w-full h-14 px-4 rounded-xl border border-slate-200 bg-white text-slate-900 outline-none focus:ring-2 focus:ring-[#00357C]/30 focus:border-[#00357C] transition"
          >
            <option value="">Selecione</option>
            <option value="M">Masculino</option>
            <option value="F">Feminino</option>
          </select>
        </label>

        <label className="block">
          <span className="text-sm font-medium text-slate-700">Convênio</span>
          <select
            value={convenioId}
            onChange={(e) => setConvenioId(e.target.value)}
            disabled={loadingConvenios}
            className="mt-2 w-full h-14 px-4 rounded-xl border border-slate-200 bg-white text-slate-900 outline-none focus:ring-2 focus:ring-[#00357C]/30 focus:border-[#00357C] transition"
          >
            {loadingConvenios && <option>Carregando convênios...</option>}
            {!loadingConvenios &&
              convenios.map((c) => (
                <option key={`${c.id}-${c.nome}`} value={c.id || ""}>
                  {c.nome}
                </option>
              ))}
          </select>
          <p className="text-xs text-slate-400 mt-1">
            Se você não possui convênio, mantenha como Particular / Sem convênio.
          </p>
        </label>

        {error && (
          <div className="flex items-start gap-2 bg-red-50 text-red-700 border border-red-100 rounded-xl px-4 py-3 text-sm">
            <AlertCircle size={16} className="mt-0.5 shrink-0" /> {error}
          </div>
        )}

        <PrimaryButton type="submit" disabled={!valid || loading}>
          {loading ? "Salvando..." : "Continuar"} {!loading && <ArrowRight size={18} />}
        </PrimaryButton>
      </form>
    </Layout>
  );
}